import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function BugArrivalChart({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(320);
  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <ComposedChart
        data={points}
        margin={{ ...dynamicsBucketChartMargins }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
          if (onPointClick && typeof label === 'string') onPointClick(label);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <Legend />
        <Bar dataKey="created" name="Created" fill="#ef4444" />
        <Bar dataKey="resolved" name="Resolved" fill="#22c55e" />
        <Line type="monotone" dataKey="net" name="Net backlog Δ" stroke="#0ea5e9" strokeWidth={2} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export const DynamicsC_BugArrivalVsClosure: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="C"
      title="Bug arrival vs closure"
      hint="Created bugs (bars) vs resolved bugs (line). Net backlog change shows whether we are catching up or falling behind."
      needs="Bug-like issue types + resolution dates synced. Works with any Group by."
      metric="bug-arrival"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={320}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.C.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.C.bullets]}
    >
      {(points, onPointClick) => <BugArrivalChart points={points} onPointClick={onPointClick} />}
    </DynamicsChartFrame>
  );
};
